import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ClearTheChartsContext } from '../ClearTheChartsContext'
import ProgressBar from '../components/ProgressBar'
import UserAnswers from '../components/UserAnswers'
import {
  calculateBets,
  matrix,
  getRandomIntInclusive,
  calculateAnswerPoints,
} from '../components/utils'
import TableView from '../table'
import { BottomSheet } from 'react-spring-bottom-sheet'
import 'react-spring-bottom-sheet/dist/style.css'
import ChartGrid from '../components/ChartGrid'
import { getMediaUrl } from '../../../utils/utils'
import GameOverImage from '../../../icons/gameOver.png'
import ChartName from '../components/ChartName'
import ChartActions from '../components/ChartActions'
import { ChartHeader } from '../components/ChartHeader/ChartHeader'
import useElementSize from '../../../hooks/useElementSize'
import useWindowSize from '../../../hooks/useWindowSize'
import { saveCurrentChartProgress, saveUserStats } from '../api'
import ChartCleared from '../components/ChartCleared'

const TableContainer = ({
  chartData: { chartData },
  colors,
  isBottomSheetOpen,
  onBottomSheetDismiss,
  selectedFilters,
  mistakesCount,
  setShowMistakes,
  setMistakesCount,
  isResume,
}) => {
  const availableActions = useMemo(
    () => (chartData?.actions ? JSON.parse(chartData?.actions) : []),
    [chartData?.actions],
  )

  const [hero, setHero] = useState('')
  const [hand, setHand] = useState([])
  const [handActionsData, setHandActionsData] = useState({})
  const [answer, setAnswer] = useState({})
  const [loading, setLoading] = useState(false)
  const [playersBets, setPlayersBets] = useState({})
  const [matrixActions, setMatrixActions] = useState({})
  const [hands, setHands] = useState([])
  const [isGameOver, setIsGameOver] = useState(false)
  const [isChartCleared, setChartCleared] = useState(false)
  const [fixedMistakes, setFixedMistakes] = useState([])
  const [mistakeToDrill, setMistakeToDrill] = useState(null)
  const [fixesInProgress, setFixesInProgress] = useState({})

  const [containerRef, { width: containerWidth }] = useElementSize()
  const { width: windowWidth } = useWindowSize()
  const [bottomSheetRef, { width: bottomSheetWidth }] = useElementSize()
  const isMobile = windowWidth < 875

  const { state, dispatch } = useContext(ClearTheChartsContext)

  const { userStats, currentChartProgress } = state

  const initialChartProgress = {
    completedHands: {},
    mistakenHands: {},
  }

  const drillMyMistakes = false
  const chart = chartData

  const { completedHands, mistakenHands } =
    currentChartProgress[chart?.id] || initialChartProgress

  const resetCurrentChartProgress = useCallback(() => {
    saveCurrentChartProgress(chart.id, {
      mistaken_hands: {},
      completed_hands: {},
    })
    setMistakesCount(0)
  }, [chart?.id])

  const restartChart = useCallback(() => {
    resetCurrentChartProgress()
    dispatch({
      type: 'reset-current-chart-progress',
      payload: { chartId: chart.id },
    })
    setIsGameOver(false)
    setChartCleared(false)
    setMistakesCount(0)
    setAnswer({})
  }, [
    chart?.id,
    dispatch,
    resetCurrentChartProgress,
    setIsGameOver,
    setChartCleared,
    setMistakesCount,
    setAnswer,
  ])

  const changeHand = useCallback(
    ({
      completedHands,
      availableHands = hands,
      actionsData = matrixActions,
      isDrillMyMistakes,
    }) => {
      const timeout = isDrillMyMistakes ? 0 : 1000
      setTimeout(() => {
        resetTable()
        let cards
        if (isDrillMyMistakes) {
          const currentHand = mistakeToDrill.hand
          cards = [currentHand.split(',')[0], currentHand.split(',')[1]]
          setHand(cards)
          setAnswer({})
        } else {
          const completedHandsToCheck = Object.keys(completedHands).map(
            (item) => {
              const [hand1, hand2] = item.split(',')
              return `${hand1} ${hand2}`
            },
          )

          const handsToRandom = availableHands.filter(
            (item) => !completedHandsToCheck.includes(item),
          )
          if (!handsToRandom.length) {
            setChartCleared(true)
            dispatch({
              type: 'set-sweeper-chart-completed',
              payload: { chartId: chart.id },
            })
            saveDataToApi({ wins: true })

            return
          }

          const randomHandIndex = getRandomIntInclusive(
            0,
            handsToRandom.length - 1,
          )

          const randomHand = handsToRandom[randomHandIndex]

          cards = randomHand.split(' ')

          setHand(cards)
        }

        const handData = {}
        const actionsKeys = Object.keys(actionsData)
        actionsKeys.forEach((key) => {
          if (key === 'OoR') return
          handData[key] = actionsData[key][matrix[`${cards[0]} ${cards[1]}`]]
        })
        setHandActionsData(handData)
      }, timeout)
    },
    [chart?.id, dispatch, hands, matrixActions, mistakeToDrill?.hand],
  )

  const checkAvailableHands = useCallback((actionsData, data, dbFileName) => {
    const handsData = {}

    const actionsKeys = Object.keys(actionsData)
    actionsKeys.forEach((key) => {
      if (key === 'OoR') return
      if (key === 'Fold' && !actionsKeys.includes('OoR')) return

      for (let i = 0; i <= 12; i++) {
        for (let j = 0; j <= 12; j++) {
          const cellValue = parseFloat(actionsData[key][matrix[`${i} ${j}`]])
          if (cellValue > 0) {
            handsData[`${i} ${j}`] = (handsData[`${i} ${j}`] || 0) + 1
          }
        }
      }
    })

    // if (drillMyMistakes) {
    // calculateBets(dbFileName, data, {
    //   stackSize,
    //   chart,
    //   isHero,
    //   setLoading,
    //   setPlayersBets,
    // })
    //   changeHand({ isDrillMyMistakes: true, actionsData })
    //   return
    // }

    let availableHands = []
    Object.entries(handsData).forEach(([key, value]) => {
      availableHands = [...availableHands, key]
    })

    if (availableHands.length === 0) {
      console.log('no hands available for this chart')
    }

    setHands(availableHands)

    const randomHandIndex = getRandomIntInclusive(0, availableHands.length)
    const randomHand = availableHands[randomHandIndex]

    const cards = [randomHand.split(' ')[0], randomHand.split(' ')[1]]
    setHand(cards)

    const handData = {}
    actionsKeys.forEach((key) => {
      if (key === 'OoR') return

      handData[key] = actionsData[key][matrix[`${cards[0]} ${cards[1]}`]]
    })
    setHandActionsData(handData)

    calculateBets(dbFileName, data, {
      chart,
      setLoading,
      setPlayersBets,
    })
  }, [])

  const getFile = useCallback(
    async (data) => {
      let chartData = data

      String.prototype.extract = function (prefix, suffix) {
        let s = this
        var i = s.indexOf(prefix)
        if (i >= 0) {
          s = s.substring(i + prefix.length)
        } else {
          return ''
        }
        if (suffix) {
          i = s.indexOf(suffix)
          if (i >= 0) {
            s = s.substring(0, i)
          } else {
            i = s.length - 1
            if (i >= 0) {
              s = s.substring(0, i)
            }
          }
        }
        return s
      }

      const dbFileName = data?.URL

      await fetch(
        `https://s3.amazonaws.com/poker-coaching/preflop_charts/${encodeURI(
          dbFileName,
        )
          .split('+')
          .join('%2B')}`,
        {
          method: 'GET',
        },
      ).then((response) =>
        response.text().then((data) => {
          const availableActions = JSON.parse(chartData.actions)

          const actionsData = {}
          const outOfRange = []

          for (let actionKey in availableActions) {
            // replace percentage values with specific labels depending on % symbol count in chart name
            const matrix = data.extract(availableActions[actionKey], 'True')
            actionsData[availableActions[actionKey]] = matrix
              .split(' ')
              .reverse()

            const matrixArray = matrix.split(' ').reverse()
            for (let key in matrixArray) {
              outOfRange[key] =
                (outOfRange[key] || 0) + parseFloat(matrixArray[key])
            }
          }
          // create objects that will allow to randomize cards based on difficulty
          // object key is hand cards, object value is number of frequences, 1 - not mixed

          // Manually calculate outOfRange Actions data
          const outOfRangeAction = outOfRange.map((item) => 1 - item)

          // add out of range data to OoR or Fold range depending on Fold availability
          if (
            availableActions.includes('Fold') ||
            availableActions.includes('FOLD')
          ) {
            actionsData['OoR'] = outOfRangeAction
          } else {
            actionsData['Fold'] = outOfRangeAction
          }

          setMatrixActions(actionsData)

          checkAvailableHands(actionsData, chartData, dbFileName)
        }),
      )
    },
    [checkAvailableHands, setMatrixActions],
  )

  useEffect(() => {
    if (drillMyMistakes) return

    if (chart) {
      if (!isResume) {
        dispatch({
          type: 'reset-current-chart-progress',
          payload: { chartId: chart.id },
        })
        resetCurrentChartProgress()
      }

      setIsGameOver(false)
      getFile(chart)
      setHero(chart.position)
    }
  }, [
    chart,
    getFile,
    dispatch,
    drillMyMistakes,
    isResume,
    resetCurrentChartProgress,
  ])

  useEffect(() => {
    setShowMistakes(!!chartData)
  }, [chartData, setShowMistakes])

  const saveDataToApi = useCallback(
    (data) => {
      const { completedHand, mistakenHand, wins: isCompleted } = data

      const {
        completedHandsAllTime,
        mistakenHandsAllTime,
        wins,
        activeMistakes,
      } = userStats[chart.id] || {
        completedHandsAllTime: {},
        mistakenHandsAllTime: {},
        activeMistakes: {},
        wins: 0,
      }

      const currentChartProgressData = state.currentChartProgress[chart.id] || {
        completedHands: {},
        mistakenHands: {},
      }
      const { completedHands, mistakenHands } = currentChartProgressData

      const handKey = `${hand[0]},${hand[1]}`
      const previousCompletedCount = completedHandsAllTime
        ? completedHandsAllTime[handKey]
        : 0
      const previousMistakenCount = mistakenHandsAllTime
        ? mistakenHandsAllTime[handKey]
        : 0
      const previousActiveMistakesCount = activeMistakes
        ? activeMistakes[handKey]
        : 0
      const previousWinsCount = wins || 0

      const dataToSet = {}
      const chartProgressToSet = {}
      if (completedHand) {
        dataToSet.completed_hands_all_time = {
          ...completedHandsAllTime,
          [handKey]: (previousCompletedCount || 0) + 1,
        }
        chartProgressToSet.completed_hands = {
          ...completedHands,
          [handKey]: (completedHands[handKey] || 0) + 1,
        }
      }
      if (mistakenHand) {
        dataToSet.mistaken_hands_all_time = {
          ...mistakenHandsAllTime,
          [handKey]: (previousMistakenCount || 0) + 1,
        }
        dataToSet.active_mistakes = {
          ...activeMistakes,
          [handKey]: (previousActiveMistakesCount || 0) + 1,
        }
        chartProgressToSet.mistaken_hands = {
          ...mistakenHands,
          [handKey]: (mistakenHands[handKey] || 0) + 1,
        }
      }
      if (isCompleted) {
        dataToSet.wins = 1 + parseInt(previousWinsCount)
      }

      saveCurrentChartProgress(chart.id, chartProgressToSet)
      saveUserStats(chart.id, dataToSet)
    },
    [chart?.id, hand, state?.currentChartProgress, userStats],
  )

  const calculateAnswer = useCallback(
    (answer) => {
      const answerValuesArray = Object.values(handActionsData).map((value) => {
        return parseFloat(value)
      })
      const bestAnswer = Math.max(...answerValuesArray)
      const userAnswer = parseFloat(handActionsData[answer] || 0)
      const handKey = `${hand[0]},${hand[1]}`

      const userStatsToSave = {}

      const isMistake = userAnswer === 0

      let points = 0
      if (userAnswer !== 0) {
        const percentage = userAnswer * 100
        points = calculateAnswerPoints(percentage)

        if (drillMyMistakes) {
          const { id, chartId } = mistakeToDrill
          const mistakeFixesCount = fixesInProgress[id] || 0
          if (mistakeFixesCount === 2) {
            setFixedMistakes([...fixedMistakes, id])
            dispatch({
              type: 'fix-chart-hand-mistake',
              payload: {
                chartId,
                hand,
              },
            })
          } else {
            setFixesInProgress({
              ...fixesInProgress,
              [id]: mistakeFixesCount + 1,
            })
          }
        }
      } else {
        const percentage = userAnswer * 100
        points = calculateAnswerPoints(percentage)

        if (drillMyMistakes) {
          const { id } = mistakeToDrill
          const fixesObject = fixesInProgress
          delete fixesObject[id]
          setFixesInProgress(fixesObject)
        }

        if (!drillMyMistakes) {
          setMistakesCount(mistakesCount + 1)
          userStatsToSave.mistakenHand = handKey
        }
      }

      if (!drillMyMistakes) {
        const mistakesIncrement = isMistake ? 1 : 0
        const isGameOver = mistakesCount + mistakesIncrement >= 5

        const completedHandsCount = completedHands[handKey] || 0
        userStatsToSave.completedHand = handKey

        dispatch({
          type: 'set-user-sweeper-hand',
          payload: {
            chartId: chart.id,
            hand: handKey,
            isMistake,
          },
        })

        saveDataToApi(userStatsToSave)

        if (isGameOver) {
          setTimeout(() => {
            setIsGameOver(true)
            resetCurrentChartProgress()
          }, 1000)
        } else {
          changeHand({
            completedHands: {
              ...completedHands,
              [handKey]: 1 + completedHandsCount,
            },
          })
        }
      }

      setAnswer({ answer, points })

      // if (drillMyMistakes) {
      //   setTimeout(() => {
      //     getMistakeToDrill();
      //   }, 1000);
      // }
    },
    [
      chart?.id,
      changeHand,
      completedHands,
      dispatch,
      drillMyMistakes,
      fixedMistakes,
      fixesInProgress,
      hand,
      handActionsData,
      mistakeToDrill,
      mistakesCount,
      resetCurrentChartProgress,
      saveDataToApi,
      setMistakesCount,
    ],
  )

  const GameOver = ({
    chartData,
    completedHands,
    mistakenHands,
    matrixActions,
    colors,
    hand,
  }) => {
    const [animation, setAnimation] = useState({
      scale: 0,
      translate: '100vh',
      opacity: 0,
    })

    useEffect(() => {
      setAnimation({
        scale: 1,
        translate: 0,
        opacity: 1,
      })
    }, [])

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: animation.opacity,
        }}
      >
        <img
          alt=""
          style={{
            width: '250px',
            height: '200px',
            transform: `scale(${animation.scale})`,
            transition: 'transform 2000ms',
          }}
          src={getMediaUrl(GameOverImage)}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            transform: `translateY(${animation.translate})`,
            transition: 'transform 2000ms',
          }}
        >
          <ChartName chart={chartData} />
          <ChartGrid
            isGameOverMode={true}
            isSweeper={true}
            completedHands={completedHands}
            mistakenHands={mistakenHands}
            showRanges={true}
            showOutOfRange={true}
            matrixActions={matrixActions}
            colors={colors}
            hand={hand}
          />
          <div
            onClick={restartChart}
            style={{
              backgroundColor: '#0085FF',
              display: 'flex',
              width: '100%',
              borderRadius: '7px',
              padding: '10px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
          >
            <div
              style={{
                color: 'white',
                fontFamily: ' Roboto, sans-serif',
                textTransform: 'uppercase',
                textAlign: 'center',
                width: '100%',
              }}
            >
              Play Again
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderGameContent = useMemo(() => {
    if (!chartData) {
      return <TableView hero="SB" hand={[0, 0]} />
    }
    if (isChartCleared) {
      return (
        <ChartCleared
          completedHands={completedHands}
          mistakenHands={mistakenHands}
          matrixActions={matrixActions}
          colors={colors}
          hand={hand}
          chartData={chartData}
          restartChart={restartChart}
        />
      )
    }

    if (isGameOver) {
      return (
        <GameOver
          completedHands={completedHands}
          mistakenHands={mistakenHands}
          matrixActions={matrixActions}
          colors={colors}
          hand={hand}
          chartData={chartData}
        />
      )
    }

    return (
      <>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <TableView
            containerWidth={isMobile ? undefined : containerWidth}
            hero={hero}
            opponent={chart.oppPosition}
            opponentAction={chart.vsAction}
            hand={hand}
            playersBets={playersBets}
          />
          <div
            style={{ marginLeft: '75px', marginTop: '50px' }}
            className="ClearTheCharts__chart-grid"
          >
            {!!chartData && (
              <>
                <ChartGrid
                  isSweeper={true}
                  completedHands={completedHands}
                  mistakenHands={mistakenHands}
                  showRanges={true}
                  showOutOfRange={true}
                  matrixActions={matrixActions}
                  colors={colors}
                  hand={hand}
                  gridWidth={Math.min(containerWidth / 3, 400) * 1.55}
                />
                <ChartActions
                  matrixActions={matrixActions}
                  colors={colors}
                  isSweeper={true}
                />
              </>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            columnGap: '20px',
            rowGap: '20px',
            flexWrap: 'wrap',
            marginTop: '40px',
            justifyContent: 'center',
            width: '100%',
            maxWidth: '500px',
            minWidth: '300px',
            alignSelf: 'flex-start',
            // marginLeft: '30px',
          }}
        >
          <UserAnswers
            userAnswer={answer}
            calculateAnswer={calculateAnswer}
            handActionsData={handActionsData}
            availableActions={Object.keys(matrixActions)}
          />
        </div>
      </>
    )
  }, [
    isGameOver,
    hero,
    hand,
    answer,
    calculateAnswer,
    handActionsData,
    availableActions,
    completedHands,
    mistakenHands,
    chartData,
    restartChart,
    isMobile,
    matrixActions,
    isChartCleared,
    colors,
    containerWidth,
    chart?.oppPosition,
    chart?.vsAction,
    playersBets,
  ])

  // const stackSize = chartData?.stackDepth

  // const isHero = (position) => {
  //   return position === hero
  // }

  const resetTable = () => {
    setAnswer({})
    setHand([])
  }

  return (
    <div ref={containerRef}>
      <div className="ClearTheCharts__table-container">
        <div className="ClearTheCharts__table-container__column">
          <ChartHeader
            mistakesCount={mistakesCount}
            showMistakes={!!chartData && !isGameOver}
            selectedFilters={selectedFilters}
          />
          {!isGameOver && !isChartCleared && (
            <ProgressBar completedHands={completedHands} hands={hands} />
          )}

          <BottomSheet
            defaultSnap={({ lastSnap, snapPoints }) =>
              lastSnap ?? Math.max(...snapPoints)
            }
            onDismiss={onBottomSheetDismiss}
            snapPoints={({ minHeight, maxHeight }) => [0, maxHeight / 1.2]}
            open={isBottomSheetOpen}
            style={{ zIndex: 12, maxWidth: 100 }}
          >
            <div ref={bottomSheetRef} style={{ margin: 30 }}>
              <ChartGrid
                gridWidth={bottomSheetWidth}
                // gridHeight={height}
                isSweeper={true}
                completedHands={completedHands}
                mistakenHands={mistakenHands}
                showRanges={true}
                showOutOfRange={true}
                matrixActions={matrixActions}
                colors={colors}
                hand={hand}
                style={{ zIndex: 12 }}
              />
              <ChartActions
                matrixActions={matrixActions}
                colors={colors}
                isSweeper={true}
              />
            </div>
          </BottomSheet>

          {renderGameContent}
        </div>
      </div>
    </div>
  )
}

export default TableContainer
